import {getPluginFieldOptions, getUsedFieldIds} from "./plugin-field-options";
import {getSelectDefaultOption, getSelectDefaultOptions} from "../utils/select-defaults";
import React, {memo, useContext, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _set from "lodash/set";
import {nanoid} from "nanoid/non-secure";

import FormLayout from "../layouts/form-layout";
import FormContext from "../form-context";
import CustomSwitch from "../controls/switch";
import {
    applyDefaultValuesForExpressions,
    clearShowsWhenFieldValues,
    getValuesIncludingHidden,
    iterateFields,
    validate
} from "../utils";
import DividerAddButton from "./divider-add-button";
import _cloneDeep from "lodash/cloneDeep";
import _isArray from "lodash/isArray";
import _isObject from "lodash/isObject";
import _has from "lodash/has";
import _get from "lodash/get";
import _unset from "lodash/unset";
import axios from "axios";
import {
    canUsePaymentFields,
    getPaymentFieldOptions,
    getPaymentFieldSelectionValue,
    hasOtherPaymentField
} from "./payment-field-availability";
import {
    normalizePaymentCustomerAccountSettings,
    PAYMENT_CUSTOMER_ACCOUNT_MODES,
    resolvePaymentCustomerAccountMode
} from "./payment-customer-account-settings";
import {getPaymentAgreement} from "../utils/payment-consent";
import {
    getHoldDurationDescription,
    getHoldDurationValidationMessage
} from "./payment-hold-settings";

const DefaultOptionCell = props => {
    const context = useContext(FormContext);
    const choice = _get(context.values, props.name.replace(/\.defaultValue$/, ''));
    const value = choice?.value ?? choice?.label;
    return <CustomSwitch type="switch" name={props.name} label={`Default For ${choice?.label || 'Choice'}`}
        labelClassName="sr-only" small flipLayout
        value={!!value && context.values.defaultValue === value}
        disabled={!getSelectDefaultOptions({...context.values, options: [choice]}).length}
        onChange={(event, name, checked) => context.onSelectDefaultChange(checked ? value : undefined)}/>;
};

const getFieldBuilderValues = (field, formId) => {
    const values = normalizePaymentCustomerAccountSettings({...field});
    if (formId !== 'product_template_custom_fields_form' || values.type !== 'select'
        || values.multiple || values.href || !Array.isArray(values.options)) return values;
    return {...values, options: values.options.map(option => _isObject(option) ? {
        ...option,
        customValue: option.customValue ?? (option.value !== undefined && option.value !== null && option.value !== option.label)
    } : option)};
};

const getPaymentAgreementPreview = values => {
    const accountMode = resolvePaymentCustomerAccountMode(values);
    const previews = accountMode === PAYMENT_CUSTOMER_ACCOUNT_MODES.optional
        ? [
            {label: "Guest Payment", text: getPaymentAgreement(values, true)},
            {label: "Account + Saved Card", text: getPaymentAgreement(values)}
        ]
        : [{
            label: accountMode === PAYMENT_CUSTOMER_ACCOUNT_MODES.none
                ? "Guest Payment"
                : "Account + Saved Card",
            text: getPaymentAgreement(values, accountMode === PAYMENT_CUSTOMER_ACCOUNT_MODES.none)
        }];

    return (
        <div className="flex flex-col gap-2">
            {previews.map(preview => (
                <div key={preview.label || "agreement"}>
                    {preview.label ? <span className="font-semibold">{preview.label}: </span> : null}
                    {preview.text || "Complete the amount settings to preview the customer agreement."}
                </div>
            ))}
        </div>
    );
};

const getPaymentAccessDescription = values => {
    const accountMode = resolvePaymentCustomerAccountMode(values);

    if (accountMode === PAYMENT_CUSTOMER_ACCOUNT_MODES.none) {
        return "Customers pay as guests. The card is not saved.";
    }

    if (accountMode === PAYMENT_CUSTOMER_ACCOUNT_MODES.optional) {
        return values.allowInlineRegistration === false
            ? "Customers can pay as a guest or sign in to use saved cards."
            : "Customers can pay as a guest, sign in, or create an account to save the card.";
    }

    return values.allowInlineRegistration === false
        ? "Customers must sign in. New cards are saved to their account."
        : "Customers must sign in or create an account. New cards are saved.";
};

const getAllowAccountCreationDescription = values => values.mode === "take-payment"
    ? "When this is off, customers must sign in."
    : "An account is required because the card can be used again. When this is off, customers must sign in.";

const DEFAULT_RATING_OPTIONS = [
    {label: "Excellent", value: 5},
    {label: "Great", value: 4},
    {label: "OK", value: 3},
    {label: "Poor", value: 2},
    {label: "Very Poor", value: 1},
];

const getOptions = (parentForm, additionalFields, controlId) => {
    // Array of id's used on the form
    const entityFieldIds = getUsedFieldIds(parentForm?.customFields).filter(id => id !== controlId);
    const options = [
        {label: "Checkbox", value: "checkbox"},
        {label: "Currency", value: "currency"},
        {label: "Colour Picker", value: "colour-picker"},
        {label: "Date", value: "date"},
        {label: "Date & Time", value: "datetime-local"},
        {label: "Email", value: "email"},
        {label: "Telephone", value: "tel"},
        {label: "Time", value: "time"},
        {label: "Long Text", value: "textarea"},
        {label: "Rich Text", value: "rich-text"},
        {label: "Multi Selection", value: "checkbox-list"},
        {label: "Short Text", value: "text"},
        {label: "Optional Input", value: "optional-input"},
        {label: "Hidden Field", value: "hidden"},
        {label: "Number", value: "number"},
        {label: "Signature Pad", value: "signature-pad"},
        {label: "Single Selection", value: "select"},
        {label: "Rating", value: "rating", defaultOptions: DEFAULT_RATING_OPTIONS},
        {label: "Combo Box", value: "combobox"},
        {label: "Switch", value: "switch"},
        {label: "Gallery", value: "gallery"},
        {label: "Action", value: "action-input"},
        {label: "Video", value: "video-input"},
        {label: "Address", value: "address"},
        {label: "Phone Number", value: "phone-number-input"},
        ...getPaymentFieldOptions(
            parentForm?.appContext,
            hasOtherPaymentField(parentForm?.customFields, controlId),
            parentForm?.paymentFieldsDisabledReason
        ),
        {label: "Form Divider", value: "divider", group: {label: "Layout"}},
        {label: "Text", value: "form-text", group: {label: "Layout"}},
        {label: "Step", value: "step", group: {label: "Layout"}},


        // Table and list components are not supported at the moment in the field builder. They require some more thought
        // before setting it up
        // {label: "Table", value: "table"},
        // {label: "List", value: "list"},

        {label: "Radio Group", value: "radio"},
        {label: "Image", value: "image-uploader"},
        {label: "Video", value: "video-uploader"},
        {label: "Document", value: "document-uploader"},
        {label: "Availability Slot Picker", value: "availability-slot-picker"},
    ]

    // Add additional fields to options. Filter out any fields which have already been added.
    if (additionalFields) options.push(...additionalFields.filter(f => !entityFieldIds?.includes(f.id)))

    if (parentForm?.plugins) for (const key in parentForm?.plugins) if (parentForm?.plugins[key]?.ControlDisplaySettings?.()) options.push(parentForm?.plugins[key]?.ControlDisplaySettings());

    return options.filter(o => !!o)
}

const SETTINGS = (parentForm, controlType, additionalFields, context, controlId) => {
    const paymentFieldsAvailable = canUsePaymentFields(parentForm?.appContext);
    const formSettings = {
        layout: "simple-stacked",
        fieldsets: [
            {
                fields: [
                    {
                        label: "Field Type",
                        type: "select",
                        name: "fieldId",
                        options: getOptions(parentForm, additionalFields, controlId),
                        placeholder: "Select a type",
                        required: true
                    },
                    {
                        type: "hidden",
                        name: "type",
                        required: true
                    }
                ]
            }
        ]
    }
    if (!controlType?.startsWith('plugins')) {
        formSettings.fieldsets[0].fields.push(
            ...(!paymentFieldsAvailable ? [{
                type: "info",
                title: "Finish setting up Stripe",
                value: "Connect Stripe before saving or publishing a form with a payment field.",
                action: {
                    label: "Open payment settings",
                    href: "/settings/payments"
                },
                showsWhen: "type === 'payment'"
            }] : []),
            {
                label: "Label",
                type: "text",
                name: "label",
                required: true,
                showsWhen: "type !== 'divider' && type !== 'form-text' && type !== 'step'"
            },
            {
                label: "Location",
                type: "combobox",
                name: "locationId",
                showsWhen: "type === 'availability-slot-picker'",
                href: `/api/v2/departments?activeOnly=true`,
                required: true,
                pageable: true,
                fuzzySearch: true,
                canCreate: false,
                valueFieldPath: "id",
                labelFieldPath: "departmentName"
            },
            {
                label: "Session Length",
                type: "combobox",
                name: "sessionLength",
                showsWhen: "type === 'availability-slot-picker'",
                required: true,
                description: "Select how long each availability slot should be. This controls how many time slots can fit into each day",
                fuzzySearch: true,
                canCreate: false,
                value: 30,
                options: [
                    {label: '15 minutes', value: 15},
                    {label: '30 minutes', value: 30},
                    {label: '45 minutes', value: 45},
                    {label: '1 hour', value: 60},
                    {label: '1 hour 15 minutes', value: 75},
                    {label: '1 hour 30 minutes', value: 90},
                    {label: '1 hour 45 minutes', value: 105},
                    {label: '2 hours', value: 120}
                ]
            },
            {
                label: "Help Text",
                description: "Provide any information to help the form-filler complete this field",
                type: "text",
                name: "description",
                showsWhen: "type !== 'radio' && type !== 'checkbox-list' && type !== 'colour-picker' && type !== 'divider' && type !== 'form-text' && type !== 'step' && type !== 'hidden'"
            },
            {
                label: "Let the customer choose the amount",
                type: "switch",
                flipLayout: true,
                small: true,
                name: "customerEntersAmount",
                showsWhen: "type === 'payment' && mode !== 'card-capture'"
            },
            {
                label: "Amount",
                type: "currency",
                name: "fixedAmount",
                required: true,
                showsWhen: "type === 'payment' && mode !== 'card-capture' && customerEntersAmount !== true"
            },
            {
                label: "Minimum amount",
                type: "currency",
                name: "minimumAmount",
                required: true,
                showsWhen: "type === 'payment' && mode !== 'card-capture' && customerEntersAmount === true"
            },
            {
                label: "Maximum amount",
                type: "currency",
                name: "maximumAmount",
                required: true,
                showsWhen: "type === 'payment' && mode !== 'card-capture' && customerEntersAmount === true"
            },
            {
                label: "Currency",
                type: "combobox",
                name: "currency",
                required: true,
                canCreate: false,
                fuzzySearch: true,
                href: "/api/v2/locale/currencies/valid-for-sales",
                labelFieldPath: "englishName",
                valueFieldPath: "threeCharacterCode",
                value: parentForm?.appContext?.currency || "AUD",
                showsWhen: "type === 'payment' && mode !== 'card-capture'"
            },
            {
                label: "How long should the hold last? (hours)",
                description: getHoldDurationDescription,
                type: "number",
                name: "holdDurationHours",
                required: true,
                min: 1,
                max: 168,
                step: 1,
                value: 72,
                validateOnChange: getHoldDurationValidationMessage,
                errorMessage: "Enter a whole number between 1 and 168 hours.",
                showsWhen: "type === 'payment' && mode === 'security-hold'"
            },
            {
                label: "Allow this hold to be taken as payment",
                description: "When enabled, the hold may later be charged. Customers must agree before the hold is placed.",
                type: "switch",
                flipLayout: true,
                small: true,
                name: "allowHoldCapture",
                value: false,
                showsWhen: "type === 'payment' && mode === 'security-hold'"
            },
            {
                label: "When may this hold be taken as payment?",
                description: "Complete the sentence after ‘if’, for example: ‘the customer does not complete the agreed purchase’.",
                type: "textarea",
                name: "holdCaptureTerms",
                placeholder: "the customer does not complete the agreed purchase",
                required: true,
                showsWhen: "type === 'payment' && mode === 'security-hold' && allowHoldCapture === true"
            },
            {
                label: "Customer Access",
                type: "select",
                name: "customerAccountMode",
                required: true,
                disablePlaceholder: true,
                value: PAYMENT_CUSTOMER_ACCOUNT_MODES.required,
                description: getPaymentAccessDescription,
                showsWhen: "type === 'payment' && mode === 'take-payment'",
                options: [
                    {
                        label: "Guest Checkout",
                        value: PAYMENT_CUSTOMER_ACCOUNT_MODES.none
                    },
                    {
                        label: "Guest Checkout Or Account",
                        value: PAYMENT_CUSTOMER_ACCOUNT_MODES.optional
                    },
                    {
                        label: "Account Required",
                        value: PAYMENT_CUSTOMER_ACCOUNT_MODES.required
                    }
                ]
            },
            {
                label: "Offer Account Creation",
                description: "When this is on, guests can create an account and save their card for next time.",
                type: "switch",
                flipLayout: true,
                small: true,
                name: "allowInlineRegistration",
                value: true,
                showsWhen: "type === 'payment' && mode === 'take-payment' && customerAccountMode === 'optional'"
            },
            {
                label: "Allow Account Creation",
                description: getAllowAccountCreationDescription,
                type: "switch",
                flipLayout: true,
                small: true,
                name: "allowInlineRegistration",
                value: true,
                showsWhen: "type === 'payment' && mode !== 'take-payment' || type === 'payment' && customerAccountMode === 'required'"
            },
            {
                label: "Extra Agreement Text",
                description: "Added after the standard payment and card-saving agreement.",
                type: "textarea",
                name: "consentText",
                placeholder: "Add any terms specific to this form",
                rows: 2,
                required: false,
                showsWhen: "type === 'payment'"
            },
            {
                type: "details",
                name: "paymentAgreementPreview",
                title: "Preview Customer Agreement",
                value: getPaymentAgreementPreview,
                optional: false,
                showsWhen: "type === 'payment'"
            },
            {
                type: "checkbox-list",
                label: "Address fields",
                description: "Choose which fields to show for address",
                name: "fields",
                value: context?.values?.fields ? context.values.fields : ["postcode", "suburb", "addressLine1", "country", "state", "addressLine2"],
                options: [
                    {label: "Address Line 1", value: "addressLine1"},
                    {label: "Address Line 2", value: "addressLine2"},
                    {label: "Suburb", value: "suburb"},
                    {label: "State", value: "state"},
                    {label: "Postcode", value: "postcode"},
                    {label: "Country", value: "country"},
                ],
                showsWhen: "type === 'address'"
            },
            {
                label: "Categories",
                type: "table",
                name: "categories",
                showsWhen: "type === 'gallery'",
                sortable: true,
                editable: true,
                onItemAdd: (e, name, values, item) => {
                    return {...item, id: nanoid()};
                },
                fieldsets: [
                    {
                        fields: [
                            {
                                name: "id",
                                type: "hidden",
                            },
                            {
                                label: "Title",
                                name: "title",
                                type: "text",
                                required: true,
                            }
                        ]
                    }
                ],
            },
            {
                label: "Website Layout",
                type: "select",
                name: "websiteLayout",
                value: "grid",
                showsWhen: "type === 'gallery'",
                options: [{label: "Grid", value: "grid"}, {label: "Carousel", value: "carousel"}],
                description: "Choose a wrapping grid or a scrolling row on the website."
            },
            {
                label: "Design",
                type: "select",
                name: "design",
                value: "cards",
                showsWhen: "type === 'gallery'",
                options: [{label: "Cards", value: "cards"}, {label: "Thumbnails", value: "thumbnails"}],
                description: "Cards fill each tile with the image. Thumbnails fit the complete image inside each tile."
            },
            {
                label: "Custom Field Name",
                type: "switch",
                name: "customFieldName",
                showsWhen: values => values.type !== 'divider' && !values.fixedFieldId
            },
            {
                type: "text",
                name: "fieldName",
                placeholder: 'Custom field name',
                required: true,
                showsWhen: values => values.customFieldName === true && !values.fixedFieldId
            },
            {
                label: "Input Type", type: "select", name: "inputType", value: "text",
                showsWhen: "type === 'optional-input'",
                options: [{label: "Text", value: "text"}, {label: "Email", value: "email"},
                    {label: "Phone", value: "tel"}, {label: "URL", value: "url"}]
            },
            {label: "Default Summary", type: "text", name: "defaultText", showsWhen: "type === 'optional-input'"},
            {label: "Reveal Action", type: "text", name: "revealLabel", showsWhen: "type === 'optional-input'"},
            {label: "Reset Action", type: "text", name: "resetLabel", showsWhen: "type === 'optional-input'"},
            {
                label: "Mark as Required",
                description: "The form-filler must complete this field",
                type: "switch",
                flipLayout: true,
                small: true,
                name: "required",
                showsWhen: "type !== 'checkbox-list' && type !== 'colour-picker' && type !== 'divider' && type !== 'form-text' && type !== 'step' && type !== 'hidden' && type !== 'payment'"
            },
            {
                label: "Default Value",
                type: "text",
                name: "value",
                showsWhen: "type === 'hidden'"
            },
            {
                type: 'select',
                label: 'Size',
                name: 'rows',
                options: [
                    {label: 'Small', value: 4},
                    {label: 'Medium', value: 6},
                    {label: 'Large', value: 8},
                ],
                value: 6,
                showsWhen: "type === 'textarea'",
                required: true
            },
            !context?.values?.href ? {
                label: "Options",
                type: "table",
                name: "options",
                className: parentForm.id === 'product_template_custom_fields_form'
                    && controlType === 'select' && !context?.values?.multiple
                    ? classNames(
                        '[&_table]:w-full [&_table]:table-fixed [&_th]:px-2 [&_td]:px-2',
                        '[&_th:first-child]:w-8 [&_th:first-child]:px-1 [&_td:first-child]:px-1',
                        '[&_td:nth-child(2)]:whitespace-normal [&_td:nth-child(2)]:break-words',
                        '[&_th:nth-child(3)]:w-16 [&_th:last-child]:w-16'
                    )
                    : undefined,
                showsWhen: "type === 'select' || type === 'checkbox-list' || type === 'combobox' || type === 'rating'",
                sortable: true,
                editable: true,
                fieldsets: [
                    {
                        fields: [
                            {
                                label: "Option",
                                name: "label",
                                type: "text",
                                required: true
                            },
                            ...(parentForm.id === 'product_template_custom_fields_form'
                                && controlType === 'select' && !context?.values?.multiple ? [{
                                    label: 'Default',
                                    name: 'defaultValue',
                                    type: 'select-default-option',
                                    readOnly: true
                                }] : []),
                            {
                                label: "Custom value",
                                type: "switch",
                                name: "customValue",
                            },
                            {
                                label: "Value",
                                name: "value",
                                type: "text",
                                required: true,
                                showsWhen: "customValue === true"
                            }
                        ]
                    }
                ],
                ignoreFields: ['customValue', 'value'],
                required: true
            } : null,
            !context?.values?.href ? {
                label: "Options",
                type: "table",
                name: "options",
                showsWhen: "type === 'colour-picker'",
                sortable: true,
                editable: true,
                fieldsets: [
                    {
                        fields: [
                            {
                                label: "Colour",
                                name: "colour",
                                type: "color",
                                required: true
                            }, {
                                label: "Option",
                                name: "label",
                                type: "text",
                                required: true
                            },
                            {
                                label: "Custom value",
                                type: "switch",
                                name: "customValue",
                            },
                            {
                                label: "Value",
                                name: "value",
                                type: "text",
                                required: true,
                                showsWhen: "customValue === true"
                            }
                        ]
                    }
                ],
                ignoreFields: ['customValue', 'value'],
                required: true
            } : null,
            {
                label: "Options",
                type: "select",
                name: "style",
                value: "list",
                options: [
                    {
                        label: "List",
                        value: "list"
                    },
                    {
                        label: "Cards",
                        value: "cards"
                    },
                ],
                showsWhen: "type === 'radio'",
                required: true
            },
            !context?.values?.href ? {
                label: "Options",
                type: "table",
                name: "options",
                showsWhen: "type === 'radio'",
                sortable: true,
                editable: true,
                ignoreFields: ['customValue', 'value'],
                fieldsets: [
                    {
                        fields: [
                            {
                                label: "Image",
                                type: "image-uploader",
                                className: "max-w-xs",
                                name: "image",
                                pintura: false,
                                unsplash: false
                            },
                            {
                                label: "Option",
                                name: "label",
                                type: "text",
                                required: true
                            },
                            {
                                label: "Description",
                                name: "description",
                                type: "text"
                            },
                            {
                                label: "Custom value",
                                type: "switch",
                                name: "customValue",
                            },
                            {
                                label: "Value",
                                name: "value",
                                type: "text",
                                required: true,
                                showsWhen: "customValue === true"
                            }
                        ]
                    }
                ],
                required: true
            } : null,
            {
                type: "rich-text",
                label: "Text to display",
                description: "A way to add text to your form without asking for an answer",
                name: "value",
                showsWhen: "type === 'form-text'",
                required: true,
                rows: 10
            },
            {
                type: "text",
                label: "Title",
                description: "Step title",
                name: "title",
                showsWhen: "type === 'step'",
            },
            {
                type: "rich-text",
                label: "Text",
                description: "Step text",
                name: "text",
                showsWhen: "type === 'step'",
            })
    } else {
        const currentPlugin = parentForm.pluginControls[controlType];
        if (currentPlugin?.ControlConfigurationFields?.()) formSettings.fieldsets[0].fields.push(...currentPlugin.ControlConfigurationFields());
    }

    if (["product_type_form", "product_template_custom_fields_form"].includes(parentForm.id)) formSettings.fieldsets[0].fields.push({
        label: "Hide on product detail page",
        type: "switch",
        flipLayout: true,
        small: true,
        name: "hideOnProductDetailPage",
    }, {
        label: "Hide from filters",
        type: "switch",
        flipLayout: true,
        small: true,
        name: "hideFromFilters",
    }, {
        label: "Include In Product Search",
        description: "Allow staff to find Products using values entered in this field.",
        type: "switch",
        showsWhen: "type === 'text' || type === 'textarea' || type === 'combobox' || type === 'select' || type === 'radio'",
        flipLayout: true,
        small: true,
        name: "searchable",
    })

    formSettings.fieldsets[0].fields.push({
        label: "Auto-select single value",
        description: "If there is only one value available it will be automatically selected",
        type: "switch",
        showsWhen: "type === 'combobox'",
        flipLayout: true,
        small: true,
        name: "autoSelectIfSingle",
    })

    formSettings.fieldsets[0].fields = formSettings.fieldsets[0].fields.filter(field => !!field);
    return formSettings
}

const getSettings = (parentFormContext, controlType, props, additionalFields, context, controlId) => {
    const settings = SETTINGS(parentFormContext, controlType, additionalFields, context, controlId);
    if (!_isObject(props.extendable)) return settings;

    if (props.extendable.canChangeFieldName === false) {
        settings.fieldsets[0].fields = settings.fieldsets[0].fields.filter(
            field => !field.name?.toLowerCase().includes('fieldname')
        );
    }

    if (_isArray(props.extendable.controls)) {
        // Array of id's used on the form, excluding the current one being edited
        let entityFieldIds = parentFormContext?.customFields?.find(f => f.id === 'custom-fields')?.fields?.map(f => f.id)?.filter(id => id !== controlId);
        const field = settings.fieldsets[0].fields.find(field => field.name === 'fieldId');
        const typeField = settings.fieldsets[0].fields.find(field => field.name === 'type');

        if (field) {
            field.options = getOptions(parentFormContext, null, controlId)
                .filter(option => props.extendable.controls?.includes(option.controlType || option.value));
            // Add additional fields to options. Filter out any fields which have already been added.
            if (additionalFields) field.options = [...field.options, ...additionalFields.filter(f => !entityFieldIds?.includes(f.id))];
            field.value = props.extendable.defaultDateType || "text";
            typeField.value = props.extendable.defaultDateType || "text";
        }
    }

    if (_isObject(props.extendable.copy)) {
        const fieldsetCopy = props.extendable.copy.fieldset || {};
        settings.fieldsets.forEach(fieldset => {
            if (fieldsetCopy[fieldset.id]) fieldset.label = fieldsetCopy[fieldset.id] || fieldset.label;
        });

        const fieldCopy = props.extendable.copy.field || {};
        iterateFields(settings.fieldsets, (field) => {
            if (_has(fieldCopy, field.name)) field.label = _get(fieldCopy, field.name) || field.label
        });
    }

    return settings;
}

const mapFields = (fields, groupLabel, entity) => fields.map(f => {
    delete f.fieldId;
    return entity ? {
        ...f,
        name: `${entity}.${f.id}`,
        id: `${entity}.${f.id}`,
        value: `${entity}.${f.id}`,
        group: {label: groupLabel},
    } : f
});


/**
 * Manages the edit state of a field.  The usage of this component is complicated in a sense that we a rendering
 * this component in a Fields component and using a FormLayout component which inturn uses a Fields component to render
 * the settings form of the field.
 *
 * It will take the nearest form context and create a new one to manage this field settings form instance. When this form
 * is submitted it will update the field. Depending on if this component was rendered using the fieldset builder or just
 * rendered because the fieldset is expandable but the fieldset is not editable will determine who updates the fieldset
 * in the form context.
 *
 * When this component is rendered using the FieldsetBuilder its the FieldsetBuilder's responsibility to update the
 * fieldset. This is because the fieldset is being managed by the FieldsetBuilder ranther then just rendered. If
 * rendered by itself then the parent manages updating the fieldset.
 *
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
const FieldBuilder = memo((props) => {
    // Get a reference to the parent form context. When this component is rendered in a form layout the origin of the
    // render could come from the fieldset build or form component. This will return the nearst parent form context. 
    const parentFormContext = useContext(FormContext);
    const isProductTemplate = parentFormContext.id === 'product_template_custom_fields_form';
    const formRef = useRef();
    const [editing, setEditing] = useState(props.editing);
    const [controlType, setControlType] = useState(props?.field?.type);
    const controlId = useMemo(() => props?.field?.name, [props?.field?.name]);
    const [additionalFields, setAdditionalFields] = useState([]);
    const [settings, setSettings] = useState(() => getSettings(
        parentFormContext, controlType, props, additionalFields, {values: props.field || {}}, controlId
    ));

    useEffect(() => {
        const cancelToken = axios.CancelToken.source();
        const newFields = [];
        if (props?.extendable?.additionalFields) {
            (async () => {
                for (let fields of props.extendable.additionalFields) {
                    fields.userDefinedFieldsets?.[0]?.fields
                        ? newFields.push(...mapFields(fields.userDefinedFieldsets[0].fields, fields.groupLabel, fields.entity))
                        : await axios.get(fields.href, {cancelToken: cancelToken.token})
                            .then(({data}) => {
                                if (fields.pluginFields) {
                                    for (const group of data) {
                                        newFields.push(...getPluginFieldOptions(group));
                                    }
                                } else {
                                    newFields.push(...mapFields(data, fields.groupLabel, fields.entity));
                                }
                            })
                            .catch((err) => {
                                if (!axios.isCancel(err)) console.log(err)
                            })
                }
                setAdditionalFields(newFields);
            })();
        }
        return () => cancelToken.cancel();
    }, [props?.extendable?.additionalFields])

    // Setup a form context for the field settings form
    const [context, setContext] = useState(() => {
        return {
            ...parentFormContext,
            values: props.editing
                ? getFieldBuilderValues(props.field, parentFormContext.id)
                : getValuesIncludingHidden(settings.fieldsets, settings.hiddenFields),
            errors: {},
            original: null,
            validate: (ref) => {
                return validate((ref || formRef).current, setContext);
            }
        }
    });

    useEffect(() => {
        setSettings(getSettings(parentFormContext, controlType, props, additionalFields, context, controlId))
    }, [
        props.extendable,
        controlType,
        additionalFields,
        context?.values?.options,
        context?.values?.defaultValue,
        context?.values?.multiple,
        context?.values?.href,
        parentFormContext?.paymentFieldsDisabledReason
    ])

    useEffect(() => {
        const option = additionalFields.find(field => field.id === props.field?.id && field.fieldDefaults?.fixedFieldId);
        if (option) setContext(previous => ({
            ...previous,
            values: {...previous.values, fieldId: option.value, fixedFieldId: option.id, customFieldName: false}
        }));
    }, [additionalFields, props.field?.id]);

    if (
        context?.values?.type &&
        (!context.values.fieldId || (context.values.type === "payment" && context.values.fieldId === "payment"))
    ) {
        setContext(previous => ({
            ...previous,
            values: {
                ...previous.values,
                fieldId: previous.values.type === "payment"
                    ? getPaymentFieldSelectionValue(previous.values.mode)
                    : previous.values.type
            }
        }))
    }

    useEffect(() => {
        if (formRef.current && editing) {
            formRef.current.scrollIntoView({behavior: "smooth", block: "center", inline: "nearest"})
        }
    }, [formRef, editing]);

    return (
        <div className={classNames(
            "cx-field-builder",
            "flex w-full",
            !editing ? "cx-add-field-row justify-center items-center cursor-pointer" : null,
            editing ? "flex-col" : null,
        )}>
            {!editing ? (
                <DividerAddButton
                    label={"Add Field"}
                    showLabel={parentFormContext?.id === 'website_form'}
                    onClick={() => {
                    setEditing(true)
                }}/>
            ) : null}
            {editing ? (
                <FormContext.Provider value={{...context,
                    customControls: {...context.customControls, 'select-default-option': {DisplayValue: DefaultOptionCell}},
                    onSelectDefaultChange: value => setContext(previous => {
                        const values = {...previous.values};
                        if (value !== undefined) values.defaultValue = value;
                        else delete values.defaultValue;
                        return {...previous, values};
                    })
                }}>
                    <FormLayout
                        ref={formRef}
                        {...settings}
                        className={"cx-field-builder-surface w-full bg-panels-100 rounded-xl p-4"}
                        builder={true}
                        actions={props.editing ? [
                            {label: "Done", primary: true},
                            {label: "Remove", destructive: true},
                            {label: "Cancel", back: true}
                        ] : [
                            {label: "Done", primary: true},
                            {label: "Cancel", back: true}
                        ]}
                        onFieldChange={(e, name, value) => {
                            const options = settings?.fieldsets?.[0]?.fields?.find(field => field.name === 'fieldId')?.options;
                            const option = options?.find(o => o?.value === value);
                            const selectedControlType = option?.controlType || option?.type || value;
                            if (name === 'fieldId') setControlType(selectedControlType);

                            setContext(previous => {
                                let values = _cloneDeep(previous.values);
                                const errors = _cloneDeep(previous.errors);
                                const previousOption = name === 'fieldId'
                                    ? options?.find(o => o?.value === previous.values.fieldId)
                                    : null;

                                const optionEdit = /^options\[(\d+)\]\.(label|value|customValue)$/.exec(name);
                                const previousChoice = optionEdit && previous.values.options?.[Number(optionEdit[1])];
                                const previousDefault = isProductTemplate && getSelectDefaultOption(previous.values);
                                const editingDefault = previousDefault && previousChoice
                                    && (previousChoice.value ?? previousChoice.label) === previous.values.defaultValue
                                    && getSelectDefaultOptions(previous.values)
                                        .filter(choice => choice.value === previous.values.defaultValue).length === 1;

                                _set(values, name, value);
                                if (editingDefault) {
                                    const choice = values.options[Number(optionEdit[1])];
                                    if (!choice.customValue) delete choice.value;
                                    values.defaultValue = choice.value ?? choice.label;
                                }
                                if (isProductTemplate && name === 'options' && !getSelectDefaultOption(values)) delete values.defaultValue;
                                let changedField;
                                iterateFields(settings.fieldsets, field => {
                                    if (!changedField && field?.name === name) changedField = field;
                                });
                                const liveError = typeof changedField?.validateOnChange === 'function'
                                    ? changedField.validateOnChange(value, values)
                                    : null;
                                _set(errors, name, liveError || false);

                                if (name === 'fieldId') {
                                    const currentLabel = typeof values.label === 'string' ? values.label.trim() : '';
                                    const defaultFieldLabel = props?.extendable?.copy?.field?.label;
                                    const shouldUseDefaultLabel = !props.editing && (
                                        !currentLabel ||
                                        currentLabel === defaultFieldLabel ||
                                        currentLabel === previousOption?.defaultLabel
                                    );

                                    if (values.fixedFieldId && !option?.fieldDefaults?.fixedFieldId) {
                                        delete values.fixedFieldId;
                                        delete values.id;
                                        delete values.value;
                                        delete values.options;
                                    }
                                    _set(values, 'type', selectedControlType)
                                    if (option?.fieldDefaults) values = {...values, ...option.fieldDefaults, fieldId: value};
                                    else if (option?.type) values = {...values, ...option};
                                    if (option?.defaultOptions && !values.options) {
                                        values.options = option.defaultOptions;
                                    }
                                    if (shouldUseDefaultLabel && option?.defaultLabel) values.label = option.defaultLabel;
                                }

                                if (name === 'mode' && values.type === 'payment') {
                                    _set(values, 'fieldId', getPaymentFieldSelectionValue(value));
                                }

                                if (e?.target?.options) {
                                    for (let i = 0; i < e.target.options.length; i++) {
                                        if (e.target.options[i].value === value && e.target.options[i].id) {
                                            values.id = e.target.options[i].id;
                                        }
                                    }
                                }

                                // Apply new defaults of fields that have expressions
                                applyDefaultValuesForExpressions(settings.fieldsets, name, value, values);

                                if (e?.target?.setCustomValidity) {
                                    e.target.setCustomValidity(liveError || '');
                                }

                                setTimeout(() => parentFormContext.setContext(previous => ({
                                    ...previous,
                                    paymentFieldDraft: values?.type === 'payment' ? values : null
                                })));
                                return {...previous, values, errors};
                            });
                        }}
                        onActionClick={(e, action) => {
                            e.preventDefault();
                            e.stopPropagation();

                            let values = null;
                            if (action.primary) {
                                if (!validate(formRef.current, setContext)) {
                                    return;
                                }

                                const customFieldId = context.values.customFieldName && context.values.fieldName
                                    ? context.values.fieldName
                                    : null;
                                const id = context.values.fixedFieldId || customFieldId || context.values.id || `cf_${nanoid()}`;
                                values = {
                                    ...context.values,
                                    id,
                                    name: `${props.userDefinedFieldsetsScope ? `${props.userDefinedFieldsetsScope}.` : ''}${id}`
                                };
                                
                                // Inject default fieldOptions for action-input
                                if (values.type === 'action-input' && !values.fieldOptions) {
                                    values.fieldOptions = {
                                        label: {
                                            required: false
                                        }
                                    };
                                }
                                
                                if (props?.extendable?.disablePreview) values.disabled = true;

                                // Clear fields where they are hidden by showsWhen expressions
                                clearShowsWhenFieldValues(settings.fieldsets, values);

                                // Payment collection cannot be optional: the processor must run and confirm the
                                // configured operation before the form response can be completed. Normalise the
                                // account settings after hidden values are cleared so non-payment modes remain
                                // account-backed and legacy guest fields acquire their explicit mode.
                                if (values.type === 'payment') {
                                    values = normalizePaymentCustomerAccountSettings(values);
                                    values.required = true;
                                }

                                // Handle fields with options that don't have a custom value 
                                // and set the value as the label
                                if (_isArray(values.options)) {
                                    values.options = values.options.map(option => {
                                        if (!(_isObject(option) && (option.value === undefined || option.value === null))) return option;
                                        return {...option, value: option.label};
                                    });
                                }

                                if (isProductTemplate && !getSelectDefaultOption(values)) delete values.defaultValue;

                                // Handle when the field is a radio group and it's not mandatory to allow selected
                                // option to be deselectable
                                if (values.type === 'radio' && !values.required) {
                                    values.deselectable = true;
                                } else {
                                    _unset(values, 'deselectable');
                                }

                                // Handle when the data type is a divider
                                if (values.type === 'divider') {
                                    values.divider = true;
                                    _unset(values, 'type');
                                }
                            }

                            setEditing(false);
                            parentFormContext.setContext(previous => ({...previous, paymentFieldDraft: null}));
                            setContext(previous => ({
                                ...previous,
                                values: props.editing
                                    ? getFieldBuilderValues(props.field, parentFormContext.id)
                                    : getValuesIncludingHidden(settings.fieldsets, settings.hiddenFields),
                                errors: {}
                            }));

                            props.onActionClick(e, action, values);
                        }}
                    />
                </FormContext.Provider>
            ) : null}
        </div>
    );
});

FieldBuilder.propTypes = {
    className: PropTypes.string,
    fieldset: PropTypes.object,
    field: PropTypes.object,
    editing: PropTypes.bool,
    onActionClick: PropTypes.func,
};

FieldBuilder.defaultProps = {
    onActionClick: () => {
    },
};

export default FieldBuilder;
